// Carte live de la conversation (demande du patron, 03/11) : UNE carte, ouverte dans le panneau
// de droite, que chaque demande complète ou modifie au lieu de la recréer.
//
// L'état est déduit des messages, dans l'ordre (rien d'autre à enregistrer) :
//   - visuel de carte validé (kind « map », render_visual) → couche ajoutée (c1, c2…) ; une couche
//     du même type aux mêmes éléments (60 % en commun au moins, dans les deux sens) est MISE À JOUR, pas doublée ;
//   - modification validée (kind « map_patch », modifier_carte) → mise en évidence, cadrage,
//     couche masquée, affichée ou retirée, zone de surveillance (z1, z2…).
// Chaque opération est une version (v1, v2…). Même découpage que le serveur
// (backend/geo/carteLive.js : ajouterCoucheCarte, reporterModification).
// Fonctions pures, sans React ni MapLibre (testées par node --test).

import { normalizeMapSpec } from './mapSpec.js';
import { parseTraceTime } from './traceReplay.js';

const str = (v) => (typeof v === 'string' ? v.trim() : '');
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Clé d'un nom comparée sans accents, casse ni séparateurs : « AB-1234-CI » = « ab 1234 ci ». */
export function cleNom(v) {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

export const LIVE_ARTIFACT_ID = 'carte-live';
const DEDUP_SEUIL = 0.6;

const kindOf = (a) => a?.spec?.kind ?? a?.kind ?? null;

// ── Genre d'un véhicule ou d'un engin (icône) ──────────────────────────────
// Lu dans les données (libellé, catégorie, groupe), sans accents ni majuscules ; du plus précis au
// plus général : « camion benne » est une benne, « camion citerne » une citerne. Rien de propre à
// un client : un mot absent donne une voiture.
const GENRES = [
  ['chargeuse', /\b(chargeuses?|chargeurs?|loader|tractopelles?|backhoe)\b/],
  ['pelle', /\b(pelles?|pelleteuses?|excavatrices?|excavators?)\b/],
  ['bulldozer', /\b(bulldozers?|bull|dozers?|niveleuses?|graders?)\b/],
  ['compacteur', /\b(compacteurs?|compacteuses?|rouleaux?|compresseurs?)\b/],
  ['grue', /\b(grues?|cranes?|nacelles?)\b/],
  ['benne', /\b(bennes?|tippers?|dumpers?|tombereaux?)\b/],
  ['citerne', /\b(citernes?|tankers?)\b/],
  ['remorque', /\b(remorques?|semi|trailers?|porte[- ]chars?|porte[- ]engins?)\b/],
  ['bateau', /\b(bateaux?|barges?|navires?|pirogues?|vedettes?|ferrys?|remorqueurs?|chalutiers?)\b/],
  ['ambulance', /\b(ambulances?|samu)\b/],
  ['moto', /\b(motos?|scooters?|deux[- ]roues|tricycles?|yamaha|sanya|kapanou|haojue|apsonic)\b/],
  ['minibus', /\b(minibus|mini[- ]bus|coaster|hiace|navettes?|gbakas?)\b/],
  ['bus', /\b(bus|autobus|autocars?|cars? de|sotra)\b/],
  ['camion', /\b(camions?|poids[- ]lourds?|tracteurs? routiers?|trucks?|actros|sinotruk|howo|shacman|forland|faw|dongfeng|dong feng|foton|hino|fuso)\b/],
  ['tracteur', /\b(tracteurs?|tractors?|massey|john deere)\b/],
  ['pickup', /\b(pick[- ]?ups?|hilux|ranger|l200|bt[- ]?50|navara|d[- ]?max|amarok|hunter|oroch|hardbody)\b/],
  ['4x4', /\b(4x4|land ?cruiser|prado|fortuner|rav4|patrol|pajero|suv|exeed|tucson|sportage|duster|changan|cs ?\d{2}|corolla cross|territory|everest|explorer|ecosport|cx[- ]?\d|torres|rexton|peugeot [35]008|bmw x\d|mg rx\d)\b/],
];
const PERSONNE_RE = /\b(conducteurs?|chauffeurs?|agents?|personnes?|techniciens?|employes?|livreurs?|commerciaux|commercial)\b/;
const sansAccents = (t) => String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Immatriculation de Côte d'Ivoire en tête du libellé (« 1234 AB 01 », « AB-123-CD », « CH0079 »)
const PLAQUE_RE = /^(?:\d{3,4}\s?-?[A-Z]{2}\s?-?\d{2}|[A-Z]{2}\s?-?\d{3,4}\s?-?[A-Z]{2}(?:\s?-?\d{2})?|[A-Z]{2}\d{4})\b/i;
const MOTS_VOITURE = /\b(voitures?|vehicules?|4x4|berlines?|toyota|nissan|hyundai|kia|mitsubishi|suzuki|renault|peugeot|ford|isuzu|volvo|mercedes|changan|mazda)\b/;

const genreDansTexte = (texte) => GENRES.find(([, re]) => re.test(texte))?.[0] ?? null;

/** Point qui désigne un véhicule : signal de son boîtier, placé par son nom, plaque ou modèle dans le libellé. */
export function estVehicule(point) {
  // Événement (survitesse, alerte) : à son lieu, même si son libellé commence par une plaque ;
  // jamais déplacé par les positions en direct
  if (point?.role === 'evenement' || /^lien/i.test(str(point?.geoSource))) return false;
  if (point?.signal || point?.parNom === 'vehicule') return true;
  const label = str(point?.label);
  const texte = sansAccents(`${label} ${str(point?.category)}`);
  return PLAQUE_RE.test(label) || MOTS_VOITURE.test(texte) || genreDansTexte(texte) !== null;
}

/** Trajet d'un jour passé : son marqueur reste à l'arrivée, la position du jour n'y a pas sa place. */
export function trajetPasse(trace, maintenant = Date.now()) {
  const fin = parseTraceTime(trace?.end?.time);
  return fin !== null && new Date(fin).toISOString().slice(0, 10) < new Date(maintenant).toISOString().slice(0, 10);
}

/** Genre d'un élément : engin ou véhicule (GENRES), voiture par défaut ; sinon personne ou lieu. */
export function genreElement(point, { vehicule = false } = {}) {
  if (point?.role === 'evenement' || /^lien/i.test(str(point?.geoSource))) return 'evenement';
  // Le libellé d'abord, puis la catégorie, puis les infobulles (groupe du véhicule)
  const textes = [point?.label, point?.category, ...(point?.popup ?? []).map((f) => f.text)].filter(Boolean).map(sansAccents);
  if (vehicule) {
    for (const t of textes) {
      const g = genreDansTexte(t);
      if (g) return g;
    }
    return 'voiture';
  }
  return textes.some((t) => PERSONNE_RE.test(t)) ? 'personne' : 'lieu';
}

/** Libellés des genres (légende de la carte). */
export const GENRE_LIBELLES = {
  voiture: 'Voiture', '4x4': '4x4', pickup: 'Pick-up', camion: 'Camion', benne: 'Benne', citerne: 'Citerne', remorque: 'Remorque', bus: 'Bus', minibus: 'Minibus', ambulance: 'Ambulance', moto: 'Moto',
  chargeuse: 'Chargeuse', pelle: 'Pelle', bulldozer: 'Bulldozer', compacteur: 'Compacteur', grue: 'Grue', tracteur: 'Tracteur', bateau: 'Bateau', personne: 'Personne',
};

// ── Éléments d'une couche ──────────────────────────────────────────────────
/**
 * Nom d'un élément : l'immatriculation du signal, sauf si le libellé la contient déjà ou si elle
 * n'est qu'un numéro de balise (chiffres seuls) : le libellé lisible (« M17 D55 4610 FAW »).
 * Même règle que le serveur (backend/geo/carteLive.js nomElement).
 */
export function nomElement(label, plaque) {
  const l = str(label);
  const p = str(plaque);
  if (!p) return l;
  if (!l || (!/^\d{8,}$/.test(p.replace(/\s+/g, '')) && !cleNom(l).includes(cleNom(p)))) return p;
  return l;
}
/** Éléments nommés d'une carte normalisée : [{nom, cle, lng, lat, vehicule, genre, point?}]. */
export function elementsDeSpec(spec) {
  if (!spec) return [];
  const out = [];
  const vus = new Set();
  for (const p of spec.points) {
    const vehicule = estVehicule(p);
    const nom = nomElement(p.label, vehicule ? p.signal?.plate : '');
    let cle = cleNom(nom);
    if (!cle) continue;
    // Même véhicule lu deux fois : une seule fois ; lieux et événements de même nom : tous gardés
    if (vus.has(cle)) {
      if (vehicule) continue;
      cle = `${cle}-${p.id}`;
    }
    vus.add(cle);
    out.push({ nom, cle, lng: p.lng, lat: p.lat, vehicule, genre: genreElement(p, { vehicule }), point: p });
  }
  const t = spec.trace;
  const plaque = t ? t.plate || t.vehicleName : '';
  if (plaque && !vus.has(cleNom(plaque))) {
    vus.add(cleNom(plaque));
    out.push({ nom: plaque, cle: cleNom(plaque), lng: t.end.lng, lat: t.end.lat, vehicule: true, genre: genreElement({ label: `${plaque} ${t.vehicleName}` }, { vehicule: true }), trace: true, passe: trajetPasse(t) });
  }
  for (const z of spec.zones) {
    const cle = cleNom(z.label);
    if (!cle || vus.has(cle)) continue;
    vus.add(cle);
    out.push({ nom: z.label, cle, lng: z.anchor[0], lat: z.anchor[1], vehicule: false, genre: 'zone' });
  }
  return out;
}

// ── Opérations déduites des messages ───────────────────────────────────────
/**
 * Opérations de la carte live dans l'ordre des messages : [{kind: 'map'|'map_patch', id, messageId,
 * spec}], et `enCours` : une carte ou une modification est en préparation dans la réponse en cours.
 */
export function liveMapOps(messages = []) {
  const ops = [];
  let enCours = false;
  for (const m of messages) {
    if (m?.role !== 'assistant' || m.origin === 'report') continue;
    for (const a of m.artifacts ?? []) {
      const kind = kindOf(a);
      if (kind !== 'map' && kind !== 'map_patch') continue;
      if (a.status === 'ready' && a.spec) ops.push({ kind, id: a.id, messageId: m.id, spec: a.spec });
      else if (a.status === 'pending' && m.isStreaming) enCours = true;
    }
  }
  return { ops, enCours };
}

// Ressemblance de deux couches, dans les deux sens (éléments communs sur l'ensemble des éléments) :
// un sous-ensemble (les 5 actifs parmi 39 camions) est une nouvelle couche, pas une mise à jour
const recouvrement = (a, b) => {
  if (!a.length || !b.length) return 0;
  const set = new Set(a.map((e) => e.cle));
  const communs = b.filter((e) => set.has(e.cle)).length;
  return communs / (a.length + b.length - communs);
};

function boundsDe(coords) {
  const pts = coords.filter((c) => Array.isArray(c) && isNum(c[0]) && isNum(c[1]));
  if (!pts.length) return null;
  let [w, s, e, n] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [x, y] of pts) {
    w = Math.min(w, x);
    e = Math.max(e, x);
    s = Math.min(s, y);
    n = Math.max(n, y);
  }
  const pad = 0.004;
  return [[w - pad, s - pad], [e + pad, n + pad]];
}

function libelleCouche(spec) {
  return spec.title || { markers: 'Repères', heat: 'Densité', route: 'Itinéraire', trace: 'Trajet parcouru', zones: 'Géo-clôtures' }[spec.type] || 'Carte';
}

/**
 * État de la carte après les `upTo` premières opérations (toutes par défaut) :
 * {version, couches: [{id, type, titre, visible, spec, elements, ajoutee, maj}], zones: [{id, nom,
 * centre, rayon_m, polygone, alerter, dedans, ajoutee}], evidence: {cle: {niveau, etiquette, nom}},
 * camera: {bounds, version} | null, versions: [{version, titre, genre, opId}], dernier: {…} | null}.
 */
export function etatCarte(ops, upTo = ops.length) {
  const etat = { version: 0, couches: [], zones: [], evidence: {}, camera: null, suivi: null, versions: [], dernier: null };
  let nC = 0;
  let nZ = 0;
  const elementsVisibles = () => etat.couches.filter((c) => c.visible).flatMap((c) => c.elements);
  for (const op of ops.slice(0, upTo)) {
    const version = etat.version + 1;
    let change = null;
    if (op.kind === 'map') {
      const spec = normalizeMapSpec(op.spec);
      if (!spec) continue;
      const elements = elementsDeSpec(spec);
      const meme = etat.couches.find((c) => c.type === spec.type && (spec.type === 'trace'
        ? cleNom(c.spec.trace?.plate || c.spec.trace?.vehicleName) === cleNom(spec.trace?.plate || spec.trace?.vehicleName)
        : recouvrement(c.elements, elements) >= DEDUP_SEUIL));
      if (meme) {
        Object.assign(meme, { spec, elements, titre: libelleCouche(spec), visible: true, maj: version });
        change = { genre: 'maj', titre: `${meme.titre} mise à jour`, couche: meme.id, bounds: spec.bounds };
      } else {
        nC += 1;
        const couche = { id: `c${nC}`, type: spec.type, titre: libelleCouche(spec), visible: true, spec, elements, ajoutee: version, maj: version, opId: op.id };
        etat.couches.push(couche);
        change = { genre: 'ajout', titre: couche.titre, couche: couche.id, bounds: spec.bounds };
      }
    } else {
      const p = op.spec;
      const cibles = [];
      let bounds = null;
      for (const o of Array.isArray(p.operations) ? p.operations : []) {
        if (o.action === 'mettre_en_evidence' || o.action === 'retirer_evidence' || o.action === 'cadrer' || o.action === 'suivre') {
          const noms = (o.cibles ?? []).map((c) => ({ ...c, cle: cleNom(c.nom) }));
          if (o.action === 'mettre_en_evidence') for (const c of noms) etat.evidence[c.cle] = { niveau: o.niveau || 'alerte', etiquette: str(c.etiquette), nom: c.nom, version };
          if (o.action === 'retirer_evidence') for (const c of noms) delete etat.evidence[c.cle];
          if (o.action === 'suivre' && noms[0]) etat.suivi = { cle: noms[0].cle, nom: noms[0].nom, version };
          if (o.action === 'cadrer' && o.couche) {
            const c = etat.couches.find((x) => x.id === o.couche);
            if (c) bounds = c.spec.bounds;
          }
          if (o.action !== 'retirer_evidence' && o.action !== 'suivre') {
            const cles = new Set(noms.map((c) => c.cle));
            cibles.push(...elementsVisibles().filter((e) => cles.has(e.cle)));
          }
        } else if (o.action === 'ne_plus_suivre') {
          etat.suivi = { cle: null, nom: '', version };
          // Retour à la vue d'ensemble : toutes les couches visibles et les zones
          bounds = boundsDe([...etat.couches.filter((c) => c.visible).flatMap((c) => c.spec.bounds ?? []), ...etat.zones.flatMap((z) => z.polygone)]);
        } else if (o.action === 'masquer_couche' || o.action === 'afficher_couche') {
          const c = etat.couches.find((x) => x.id === o.couche);
          if (c) c.visible = o.action === 'afficher_couche';
          if (c && o.action === 'afficher_couche') bounds = c.spec.bounds;
        } else if (o.action === 'retirer_couche') {
          etat.couches = etat.couches.filter((x) => x.id !== o.couche);
          etat.zones = etat.zones.filter((x) => x.id !== o.couche);
        } else if (o.action === 'zone_surveillance' && o.zone && Array.isArray(o.zone.polygone)) {
          nZ += 1;
          etat.zones.push({ id: `z${nZ}`, nom: str(o.zone.nom) || 'Zone', lieu: str(o.zone.lieu), centre: o.zone.centre, rayon_m: o.zone.rayon_m, polygone: o.zone.polygone, alerter: o.zone.alerter ?? ['entree', 'sortie'], dedans: o.zone.dedans ?? [], ajoutee: version, source: o.zone.source });
          bounds = boundsDe(o.zone.polygone);
        }
      }
      if (!bounds && cibles.length) bounds = boundsDe(cibles.map((e) => [e.lng, e.lat]));
      change = { genre: 'modif', titre: str(p.titre) || 'Carte modifiée', bounds };
    }
    etat.version = version;
    etat.versions.push({ version, titre: change.titre, genre: change.genre, opId: op.id, messageId: op.messageId });
    etat.dernier = { version, ...change, opId: op.id };
    if (change.bounds) etat.camera = { bounds: change.bounds, version };
  }
  return etat;
}

/** Version de la carte produite par chaque opération (identifiant d'artefact → version). */
export function versionsParOp(etat) {
  return new Map(etat.versions.map((v) => [v.opId, v]));
}

// ── Véhicules suivis en direct ─────────────────────────────────────────────
/** Noms des véhicules des couches visibles (positions relues en direct). */
export function vehiculesSuivis(etat) {
  const out = [];
  const vus = new Set();
  for (const c of etat.couches) {
    if (!c.visible) continue;
    for (const e of c.elements) {
      if (!e.vehicule || vus.has(e.cle)) continue;
      vus.add(e.cle);
      out.push(e.nom);
    }
  }
  return out.slice(0, 200);
}

/** Point dans un polygone (anneau [[lng, lat]…]). */
export function dansPolygone([x, y], anneau) {
  let dedans = false;
  for (let i = 0, j = anneau.length - 1; i < anneau.length; j = i++) {
    const [xi, yi] = anneau[i];
    const [xj, yj] = anneau[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) dedans = !dedans;
  }
  return dedans;
}

/**
 * Entrées et sorties des zones de surveillance entre deux lectures des positions.
 * presence : {zoneId: Set(cle)} de la lecture précédente (absente : première lecture, aucun
 * événement, présence initiale prise de la zone) → {presence, evenements: [{zone, nom, genre}]}.
 */
export function transitionsZones(zones, positions, presence = null) {
  const suivante = {};
  const evenements = [];
  for (const z of zones) {
    const avant = presence?.[z.id] ?? null;
    const dedans = new Set();
    for (const [cle, p] of Object.entries(positions)) if (dansPolygone([p.lng, p.lat], z.polygone)) dedans.add(cle);
    suivante[z.id] = dedans;
    if (!avant) continue;
    for (const cle of dedans) if (!avant.has(cle) && z.alerter.includes('entree')) evenements.push({ zone: z, cle, genre: 'entree' });
    for (const cle of avant) if (!dedans.has(cle) && z.alerter.includes('sortie')) evenements.push({ zone: z, cle, genre: 'sortie' });
  }
  return { presence: suivante, evenements };
}

// ── Arrêts d'une trace ─────────────────────────────────────────────────────
const enMinutes = (t) => {
  const m = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(str(t));
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0)) / 60000 : null;
};
export const heureDe = (t) => {
  const m = /[T ](\d{2}):(\d{2})/.exec(str(t));
  return m ? `${m[1]}:${m[2]}` : '';
};

/**
 * Arrêts d'une trace : positions consécutives à moins de 3 km/h pendant `minMin` minutes au moins
 * (horodatages de la trace) → [{n, lng, lat, debut, fin, duree_min}], 9 au plus.
 */
export function arretsTrace(trace, { minMin = 5, max = 9 } = {}) {
  if (!trace?.vertices?.length) return [];
  const out = [];
  let debut = null;
  const clore = (fin) => {
    if (debut === null) return;
    const a = trace.vertices[debut];
    const b = trace.vertices[fin];
    const ta = enMinutes(a.time);
    const tb = enMinutes(b.time);
    if (ta !== null && tb !== null && tb - ta >= minMin) out.push({ lng: a.lng, lat: a.lat, debut: a.time, fin: b.time, duree_min: Math.round(tb - ta) });
    debut = null;
  };
  trace.vertices.forEach((v, i) => {
    const arret = isNum(v.speed) && v.speed < 3;
    if (arret && debut === null) debut = i;
    if (!arret) clore(i);
  });
  clore(trace.vertices.length - 1);
  return out
    .sort((x, y) => y.duree_min - x.duree_min)
    .slice(0, max)
    .sort((x, y) => (enMinutes(x.debut) ?? 0) - (enMinutes(y.debut) ?? 0))
    .map((s, i) => ({ ...s, n: i + 1 }));
}

/** Cap en degrés de a vers b (0 = nord), ou null si le déplacement est négligeable. */
export function cap(a, b) {
  const dx = (b.lng - a.lng) * Math.cos((a.lat * Math.PI) / 180);
  const dy = b.lat - a.lat;
  if (Math.hypot(dx, dy) < 2e-5) return null;
  return ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
}

// Immatriculation de l'élément sélectionné : celle que les outils de données reconnaissent
function plaqueDe(etat, nom) {
  const cle = cleNom(nom);
  for (const c of etat.couches) {
    const e = c.elements.find((x) => x.cle === cle);
    const plaque = e?.point?.signal?.plate || (e?.trace ? c.spec.trace?.plate : '');
    if (plaque) return { immatriculation: plaque };
  }
  return {};
}

// ── Résumé envoyé au serveur (carte_live) ──────────────────────────────────
/**
 * État de la carte pour le modèle (backend/geo/carteLive.js lireCarteLive) : couches et zones,
 * éléments à leur dernière position connue (en direct si lue), événements récents, sélection.
 */
export function resumeCarte(etat, { positions = {}, evenements = [], selection = null } = {}) {
  if (!etat || (!etat.couches.length && !etat.zones.length)) return null;
  const couches = etat.couches.map((c) => ({
    id: c.id,
    type: c.type,
    titre: c.titre,
    visible: c.visible,
    elements: c.elements.slice(0, 200).map((e) => {
      const live = positions[e.cle];
      const ev = etat.evidence[e.cle];
      const statut = live ? (live.vitesse_kmh > 3 ? `en mouvement ${Math.round(live.vitesse_kmh)} km/h` : live.en_ligne === false ? 'hors ligne' : "à l'arrêt") : e.point?.signal?.status || '';
      return {
        nom: e.nom,
        lat: live?.lat ?? e.lat,
        lng: live?.lng ?? e.lng,
        vehicule: e.vehicule,
        ...(statut ? { statut } : {}),
        ...(ev ? { evidence: ev.niveau } : {}),
      };
    }),
  }));
  for (const z of etat.zones) couches.push({ id: z.id, type: 'surveillance', titre: z.nom, visible: true, elements: [] });
  return {
    version: etat.version,
    couches,
    evenements: evenements.slice(-10).map((e) => ({ texte: e.texte })),
    selection: selection ? { nom: selection.nom, ...plaqueDe(etat, selection.nom) } : null,
  };
}
